<template>
    <span class="app-label">
        <template
            v-for="(appItem, index) in shownApps"
            :key="`${appItem?.id ?? index}`">
            <pendo-divider
                v-if="index > 0"
                direction="vertical"
                decorative
                :stroke-width="2"
                width="16px"
                height="14px" />
            <pendo-app-display
                class="app-label__app-display"
                :class="`--${size}`"
                :apps="appItem"
                :show-label="showLabel"
                :show-icon="showIcon" />
        </template>
        <template v-if="additionalApps.length > 0">
            <pendo-popover-v2
                arrow
                placement="bottom">
                <span class="app-label__additional">+{{ additionalApps.length }}</span>
                <template #popover>
                    <div
                        v-for="(appItem, index) in additionalApps"
                        :key="`${appItem?.id ?? index}`"
                        class="app-label__additional-content">
                        <pendo-app-display
                            class="app-label__app-display"
                            :class="`--${size}`"
                            :apps="appItem"
                            :show-label="showLabel"
                            :show-icon="showIcon" />
                    </div>
                </template>
            </pendo-popover-v2>
        </template>
    </span>
</template>

<script setup>
import { PendoAppDisplay, PendoDivider, PendoPopoverV2 } from '@pendo/components';
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
    /** Single id (string), array of ids */
    app: {
        type: [String, Array],
        required: true,
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value),
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    showIcon: {
        type: Boolean,
        default: false,
    },
    showMax: {
        type: [Number, String],
        default: 1,
    },
});

const store = useStore();

const resolvedApps = computed(() => {
    const raw = props.app;
    if (raw == null) return [];

    const getAppById = store.getters['apps/getAppById'];
    if(typeof raw === 'string') {
        return [getAppById(raw)];
    }

    return raw.map((appId) => getAppById(appId));
});

const shownApps = computed(() => {
    return resolvedApps.value.slice(0, props.showMax);
});

const additionalApps = computed(() => {
    return resolvedApps.value.slice(props.showMax);
});
</script>

<style scoped lang="less">
.app-label {
    display: flex;
    flex-wrap:nowrap;
    gap: 0.5rem;
    overflow: hidden;
    align-items: center;
}
.app-label__app-display {
    &.--small,
    &.--medium {
        font-size: 12px;
    }
    &.--large {
        font-size: 14px;
    }
    &.--xlarge {
        font-size: 16px;
    }
}
.app-label__additional {
    color: var(--palette-gray-70);
    cursor: pointer;
}
</style>

<style lang="less">
.app-label__additional-content {
    .app-label__app-display {
        &.--small,
        &.--medium {
            font-size: 12px;
        }
        &.--large {
            font-size: 14px;
        }
        &.--xlarge {
            font-size: 16px;
        }
    }
}
</style>
